<script setup lang="ts">
/**
 * **一个 Area** —— 外壳 + Region 布局。
 *
 * ```
 * ┌──────────────────────────────────────────────┐
 * │ header   编辑器名 · Region 开关 · 分屏/最大化/关闭 │  ← header Region
 * ├──────┬───────────────────────────────────────┤
 * │      │                                       │
 * │toolbar│           main                        │  ← 两个 Region 并列
 * │      │      （@editor 插槽）                   │
 * ├──────┴───────────────────────────────────────┤
 * │ statusbar                                    │  ← statusbar Region
 * └──────────────────────────────────────────────┘
 * ```
 *
 * ## ⚠️ Region 是**布局盒子**，不是浮层 —— 这是这次重构的核心收益
 *
 * 重构前，左侧工具栏 / 右侧面板 / 小地图全是 `position: absolute` 浮在
 * 画布**上面**。三个后果：
 *
 *   1. **画布不知道自己被遮了多少** —— `fitView()` 会把节点送进面板底下；
 *   2. **互相让位靠手算** —— `.pg__side { bottom: 158px }` 要记住
 *      「小地图 130 + 边距 15 + 间距 13」这种算式（而且它已经漂了：手机上
 *      还留着 240×156 时代算出来的 184px）；
 *   3. **移动端互相盖** —— 输出窗口和工具栏同时开着，390px 屏上画布只剩
 *      约 36px（见 `docs/layout-redesign.md` §P3）。
 *
 * 现在 toolbar 是 main 的**兄弟**，各占自己的宽度：
 * 画布拿到的是真实的剩余空间，`fitView` 自然正确；
 * 谁也不用记住谁多大；小地图留在 main 内部当覆盖层（它是 VueFlow 的
 * 子组件，抽出来等于重写一个小地图，见 §4.4）。
 *
 * ⚠️ **右侧没有 Region**：曾经有一个 `sidebar`（放「实时调参」面板），
 * 用户实测后明确说「去掉右侧浮窗和调参窗口，没啥用」——
 * 调参面板本来也只是把设计令牌调好再固化进 `theme.css` 的临时脚手架。
 * 整条 Region 键、槽、CSS 都删了，而不是留一个空的占位。
 *
 * ## Region 的显隐
 *
 * ⚠️ 用 `v-if` 而不是 `v-show`：Region 是**布局盒子**，
 * 用 `display: none` 藏起来它仍然占着 flex 位置（`flex: 0 0 auto` 的
 * 元素隐藏后不占位，但 `v-show` 对 `flex-basis` 的语义容易出错），
 * 而且 Region 里没有需要保状态的实例（VueFlow 在 main 里，不在 Region 里）。
 * 唯一需要保状态的是**整个 Area**，那个在 `NpAreaTree` 里用 `v-show`。
 */
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { resolveAreaRegions, regionInset, useRegionTheme } from './regionPlacement'
import { useSplitDrag } from './composables/useSplitDrag'
import { useScrollMemory } from './composables/useScrollMemory'
import { scrollSelectorFor } from './viewportMemory'
import { EDITOR_META, EDITOR_ORDER, ALL_REGIONS, REGION_LABELS, editorOf,
  panelOfSide,
} from './areas'
import type { AreaLeaf, EditorType, PanelSide, RegionKey } from './areas'

const props = defineProps<{
  area: AreaLeaf
  /** 布局里一共有几个 Area —— 只剩一个时「关闭」要禁用 */
  areaCount: number
  focused: boolean
  /** 当前是否被最大化（决定 header 上的按钮图标） */
  maximized: boolean
  /**
   * 上次记下的滚动位置（`null` = 没记过）。
   *
   * ⚠️ 由宿主按 Area id 保管、组件自己负责恢复 —— 因为
   * **只有组件知道自己的主区域是哪个元素**。让宿主去 querySelector
   * 就得把 Area 的内部结构约定暴露出去。
   */
  scrollMark?: { selector: string; top: number; left: number } | null
}>()

const emit = defineEmits<{
  focus: []
  setEditor: [editor: EditorType]
  toggleRegion: [key: RegionKey]
  split: [dir: 'row' | 'col']
  /**
   * 某一侧的侧栏换成哪一页（`null` = 收起）。
   *
   * ⚠️ 状态**归布局**（`AreaLeaf.panels`），组件只发意图 ——
   * 因为「⋯」菜单里的面板开关是**宿主**生成的，两边必须读写同一份。
   */
  setPanel: [side: 'left' | 'right', tab: string | null]
  close: []
  toggleMaximize: []
  /**
   * 侧栏开合之后。
   * ⚠️ 画布宽度变了，宿主要让 Vue Flow 重新量一次（`updateNodeInternals`），
   * 否则节点/连线的位置会按旧尺寸算一帧。
   */
  panelToggled: []
  /**
   * 点开了「窗口类型」按钮。
   *
   * ⚠️ 只把**锚点元素**交出去，菜单内容由宿主决定 ——
   * 和 `areaMenu` 同一个理由：`NpArea` 是纯布局组件，
   * 它不该知道菜单模型长什么样。
   */
  editorMenu: [anchor: HTMLElement | null]
  /**
   * 右键 Area 头。
   *
   * ⚠️ 只把**坐标**交出去，菜单内容由宿主决定 ——
   * `NpArea` 是纯布局组件，它不该知道「交换」这种业务动作存在。
   */
  areaMenu: [event: MouseEvent]
  /**
   * 从「分割把手」拖出来的分割：**边界落在松手的位置**。
   *
   * ⚠️ 和 `split(dir)` 的区别就在这个 `ratio`：那两个按钮是「对半分」，
   * 这个是一把拖到位。这是拖拽分割相对按钮唯一多出来的价值 ——
   * 否则它只是一个更难用的按钮。
   */
  splitAt: [dir: 'row' | 'col', ratio: number]
  /** 主区域里某个滚动容器动了（宿主按 Area id 记下来） */
  scroll: [mark: { selector: string; top: number; left: number }]
}>()

/**
 * 插槽声明。
 *
 * ⚠️ **必须写出来**，不能靠推断。少了它，模板里的
 * `<template #toolbar="{ area }">` 拿不到 `area` 的类型，而紧接着
 * `<slot name="toolbar" :area="area" />` 又把这个未定类型往外传 →
 * `vue-tsc` 判定成循环依赖，报 TS7022（`'area' implicitly has type 'any'
 * because it is referenced directly or indirectly in its own initializer`）。
 * 声明之后类型有了确定的来源，循环断开。
 *
 * 四种插槽的 props 形状完全一样（`{ area }`）—— 这是有意的：
 * 内容是「这个 Area 放什么」，而决定放什么的是**宿主**（它持有状态），
 * 所以组件只把 Area 交出去，不做任何 `v-if editor === 'graph'` 之类的判断。
 */
defineSlots<{
  /**
   * **这个窗口自己的** header 内容（在窗口类型按钮之后、Region 开关之前）。
   * 比如节点图的「适配缩放 / 手」—— 它们是图操作，不该出现在别的窗口上。
   */
  headerExtra?: (props: { area: AreaLeaf }) => unknown
  /** 侧栏面板的内容，按 `(editor, side, tabId)` 由宿主决定渲染什么 */
  panel?: (props: { area: AreaLeaf; side: PanelSide; tab: string }) => unknown
  toolbar?: (props: { area: AreaLeaf }) => unknown
  editor?: (props: { area: AreaLeaf }) => unknown
}>()


/** 这块 Area 的根元素（拖拽分割时要量它的矩形） */
const rootEl = ref<HTMLElement | null>(null)

/**
 * ★ **header 不再「放不下就收进 ⋯」了，改成横向滚动。**
 *
 * 原来这套逻辑在 `composables/useHeaderOverflow.ts`（量宽度 + 迟滞 +
 * 收起两个按钮组）。它工作得很好，但收起等于**把功能藏起来** ——
 * 用户实测的反馈是「header 功能更多，还支持横向滚动」。
 *
 * 滚动和收起的差别不是美观：收起之后，一个功能在窄窗口里**根本不存在**，
 * 而滚动是「都在，只是要划一下」。触摸端本来就会滑动，桌面端有滚轮。
 *
 * ⚠️ 原来那套「放不下就收进 ⋯」的判定（`useHeaderOverflow` / `headerFit`）
 * **已经删掉**：收起等于把功能藏起来，而横滚是"都在，只是要划一下"。
 * 于是也不需要那个「⋯」按钮 —— 它的功能一分为二：
 *   · **面板 / 区域 / headerExtra** 回到 header 本身（横滚够得到）；
 *   · **最大化 / 关闭 / 交换内容 / 分屏** 留在**右键菜单**里（`areaSections`）。
 * 少一个按钮、少一套溢出判定，这是真机反馈里"优化代码简洁性"那一条。
 */
const headerEl = ref<HTMLElement | null>(null)
const headerScrollEl = ref<HTMLElement | null>(null)
const actionsEl = ref<HTMLElement | null>(null)
const regionGroupEl = ref<HTMLElement | null>(null)
const spacerEl = ref<HTMLElement | null>(null)
const editorBtnEl = ref<HTMLElement | null>(null)

/**
 * ★ **滚轮 / 触控板横滑这一条 header**。
 *
 * ⚠️ `overflow-x: auto` 只给了**触摸**横滑（`touch-action: pan-x` 由浏览器做），
 * 鼠标滚轮不认它：垂直滚轮在横向容器上是"没有可滚的方向"。
 * 所以这里把 `deltaY` 借给 `scrollLeft`。
 *
 * ⚠️ 两个细节：
 *   · 已经滚到头的方向**不再 preventDefault** —— 让事件继续冒泡
 *     （否则"在 header 上滚一下，页面/画布纹丝不动"会显得像卡住了）；
 *   · `deltaX`（触控板横向手势）优先，且**不**做倍率缩放。
 */
function onHeaderWheel(ev: WheelEvent): void {
  const host = headerScrollEl.value
  if (!host) return
  const max = host.scrollWidth - host.clientWidth
  if (max <= 0) return
  const delta = Math.abs(ev.deltaX) > Math.abs(ev.deltaY) ? ev.deltaX : ev.deltaY
  const next = Math.max(0, Math.min(max, host.scrollLeft + delta))
  if (next === host.scrollLeft) return
  host.scrollLeft = next
  ev.preventDefault()
}

/** 主区域（恢复滚动位置时在里面找锚点） */
const bodyEl = ref<HTMLElement | null>(null)

/**
 * 「拖出去分割」的当前状态。
 *
 * 视觉反馈（预览线）由 `NpArea` **自己**画：它知道自己那块矩形的
 * 位置和尺寸，而宿主不知道。把预览也丢给宿主的话要来回传一堆几何，
 * 而且窗口一缩放就不同步了。
 */

/*
 * ★ 分隔条拖拽（60 行）**已向下拆到** `composables/useSplitDrag.ts`。
 * 那个文件头记着「必须 setPointerCapture」以及它和变参行拖拽**相反的取舍**。
 */
const { splitDrag, onSplitGripDown } = useSplitDrag({
  rootEl: () => rootEl.value,
  splitAt: (dir, ratio) => emit('splitAt', dir, ratio),
})

/*
 * ★ 滚动位置的「报上去 / 恢复回来」（30 行）**已向下拆到**
 * `composables/useScrollMemory.ts` —— 两头必须用同一套选择器约定
 * （`viewportMemory.scrollSelectorFor`），否则会「报的是 A、找的是 B」。
 */
const { onMainScroll } = useScrollMemory({
  bodyEl: () => bodyEl.value,
  scrollMark: () => props.scrollMark,
  scrolled: (mark) => emit('scroll', mark),
})

/**
 * **这个窗口现在显示哪个编辑器** —— 全部读它，不读 `area.editor`。
 *
 * ⚠️ `area.editor` 是**意图**（可能属于一个已经被卸载的包），
 * 这里是**现状**（当前 → 默认 → 节点图，见 `editorOf`）。
 * 图标 / 名字 / Region 能力 / 侧栏 tab 都跟着它走 ——
 * 否则包一卸载，这里就会去读一个不存在的元信息。
 */
const shownEditor = computed(() => editorOf(props.area))

const supportedRegions = computed<RegionKey[]>(() => {
  const allowed = new Set(EDITOR_META[shownEditor.value].regions)
  return ALL_REGIONS.filter((key) => allowed.has(key))
})

/**
 * Region 开关的图标与名字。
 *
 * ⚠️ `minimap` 的开关在「窗口」菜单里（它是「显示项」，混进 header 会让这一行太长）。
 * `supportedRegions` 会把它过滤掉。
 */
/**
 * 头部「显示」开关。
 *
 * ⚠️ `header` **不在**这里 —— 用标题栏上的按钮把标题栏自己关掉之后，
 * 就再也点不到那个按钮了。它仍然留在右键 / 「⋯」菜单里
 * （菜单能从 Area 的其它地方打开），这样关掉了也回得来。
 *
 * ⚠️ 这一组必须和菜单里「显示」那一组**对得上**。
 * 上一版这里只有 `toolbar` 一项，而菜单列了全部 Region ——
 * 用户实测：「你新增了显示选项，但是默认的按钮组里没有」。
 */
const HEADER_REGIONS: { key: RegionKey; label: string; icon: string }[] = [
  {
    key: 'minimap',
    label: REGION_LABELS.minimap,
    icon: '<rect x="3" y="3" width="18" height="18" rx="2"/><rect x="12" y="12" width="6" height="6" rx="1"/>',
  },
]

const headerRegionButtons = computed(() =>
  HEADER_REGIONS.filter((r) => supportedRegions.value.includes(r.key)),
)

/* ══════════════════════════════════════════════════════════════════
   侧栏 tab（Blender 的 N / T 面板）

   ⚠️ 这是**窗口的基础能力**，不是某个编辑器特有的：
   编辑器在 `EDITOR_META.panels` 里声明自己有哪些 tab，
   `NpArea` 统一渲染竖排 tab 条 + 面板，内容由宿主按
   `(editor, side, tabId)` 提供。

   ⚠️ **没声明就不显示开关**。摆一个点了没反应的按钮比没有按钮更糟。
   ══════════════════════════════════════════════════════════════════ */

/** 这个编辑器的某个方向有没有 tab */
const panelsOf = computed(() => EDITOR_META[shownEditor.value].panels ?? {})

/**
 * 侧栏开着哪一页。
 *
 * ⚠️ 状态住在**组件本地**而不是布局树里：它是「这个窗口现在的样子」，
 * 不是「布局长什么样」。塞进 `AreaLeaf` 会让 `LayoutDoc` 多一个字段、
 * 多一次版本迁移，而它并不影响窗口的尺寸关系。
 * 代价是切工作区会重置 —— 可以接受（Blender 里侧栏开合也不进布局文件）。
 */
/**
 * **两个侧栏各自开合**（`left` / `right` 互不影响）。
 *
 * ⚠️ 以前是一个 `openPanel`（单值）—— 于是"点左边开左边、点右边时把左边**关掉**"
 * （真机反馈）。拆成按方向存之后，两边可以同时开着。
 *
 * ⚠️ 状态**存在布局里**（`AreaLeaf.panels`），不是组件内的 ref：
 * 真机反馈要求把左右面板按钮并进「⋯」菜单，而菜单是宿主生成的 ——
 * 组件内的局部状态它够不着。顺带"上次开着哪一页"也跟着工作区存盘了。
 */
const openTabOfSide = computed<Record<PanelSide, string | null>>(() => ({
  left: panelOfSide(props.area, 'left'),
  right: panelOfSide(props.area, 'right'),
}))

/** 设某一侧的面板（`null` = 收起）—— 交给宿主写布局，组件不自己存 */
function setPanelSide(side: PanelSide, tab: string | null): void {
  emit('setPanel', side, tab)
  layoutNudge()
}

/** 每个方向**上次看的是哪一页** —— 关掉再开应该回到那一页，而不是跳回第一页 */
const lastTabOfSide = ref<Record<PanelSide, string>>({ left: '', right: '' })

/** 这个编辑器声明了 tab 的方向（没有的方向连开关都不出现） */
const panelSides = computed<PanelSide[]>(() =>
  (['left', 'right'] as PanelSide[]).filter((side) => (panelsOf.value[side]?.length ?? 0) > 0),
)

/**
 * 某个方向当前显示的面板（换了编辑器 / 声明变了就自动失效，不会留下空壳）。
 *
 * 返回 `null` = 那一侧收起。
 */
function activePanelOf(side: PanelSide): { side: PanelSide; tab: string } | null {
  const tab = openTabOfSide.value[side]
  if (!tab) return null
  const tabs = panelsOf.value[side]
  if (!tabs?.some((t) => t.id === tab)) return null
  return { side, tab }
}

const activeLeft = computed(() => activePanelOf('left'))
const activeRight = computed(() => activePanelOf('right'))

/**
 * 侧栏一开一关会改变画布宽度 → 让图引擎重新量一次。
 *
 * ⚠️ 放在 `nextTick` 里：这一刻 DOM 还没变，量到的还是旧的宽度。
 * 这和 `useHeaderOverflow` 里那条「回调里只 schedule、真正测量放到
 * rAF」是同一条纪律 —— 读布局和写状态**不能在同一拍**里。
 */
function layoutNudge(): void {
  void nextTick(() => emit('panelToggled'))
}

/**
 * 点 header 上的侧栏开关：**只管自己这一侧**（开着就关掉，关着就回到
 * 那个方向上次看的那一页）。另一侧不受影响。
 */
function togglePanelSide(side: PanelSide): void {
  if (openTabOfSide.value[side]) {
    setPanelSide(side, null)
    return
  }
  const tabs = panelsOf.value[side]!
  const remembered = tabs.find((t) => t.id === lastTabOfSide.value[side]) ?? tabs[0]!
  setPanelSide(side, remembered.id)
}

/** 点竖排 tab 条上的一页：再点当前页就把**这一侧**收起（Blender 的行为） */
function selectTab(side: PanelSide, tab: string): void {
  const isSame = openTabOfSide.value[side] === tab
  setPanelSide(side, isSame ? null : tab)
  lastTabOfSide.value[side] = tab
}

/* ── 区域放置（主题可以改「工具栏停靠在哪 / 是不是浮起来」）─────
 *
 * ⚠️ 主题只提供**默认**：`AreaLeaf.regions` 里用户的选择优先级更高
 * （合成的规则见 `regionPlacement.ts` 的注释）。
 * 所以这里读的是「用户显隐 + 主题放置」的合成结果，不是二选一。
 */
const themeLayout = useRegionTheme()

const regions = computed(() =>
  resolveAreaRegions(shownEditor.value, props.area.regions, themeLayout.value),
)

/*
 * ⚠️ 这里原来有一整套**工具栏放置**的推导（`data-np-toolbar-edge` /
 * `-mode` / `-orientation` / `-reserve` + 四个 CSS 变量 + 画布让位的内边距）。
 *
 * 工具栏整个删掉之后它们**没有消费者**了，一并删除。
 * `regionInset()` 也只剩 `header` / `minimap` 两个 Region 在用。
 */



/** 编辑器下拉的选项 —— 顺序来自 `EDITOR_ORDER`，不在这里重排 */
const editorOptions = computed(() => EDITOR_ORDER)

/** 当前窗口类型的图标／名字 */
const editorIcon = computed(() => EDITOR_META[shownEditor.value].icon)

/**
 * 点左上角的窗口类型图标 —— **只上报锚点，不持状态**。
 *
 * ⚠️ 写成具名函数而不是模板里直接 `@click="emit('editorMenu', editorBtnEl)"`：
 * 后者在真实构建里**没有把事件发出来**（菜单根本不出现），
 * 而 `vue-tsc` 与模板编译都不报错。具名函数这条路径是确定能通的。
 */
function openEditorMenu(): void {
  emit('editorMenu', editorBtnEl.value)
}

/*
 * ⚠️⚠️ 这里原来有一个组件内的 `editorMenuOpen` **开关状态**：
 *
 * ```ts
 * editorMenuOpen.value = !editorMenuOpen.value
 * if (editorMenuOpen.value) emit('editorMenu', editorBtnEl.value)
 * ```
 *
 * 两个问题：
 *   ① **它永远不会被重置。** 菜单被点别处关掉之后它还是 `true`，
 *      于是**再点同一个图标不会重新打开**（toggle 成 false 就不发了）；
 *   ② 它是**组件内状态**，而菜单是宿主拥有的 —— 两份状态迟早不一致
 *      （上面那条 `:key` 的 bug 就是它的放大版：实例一被复用，
 *      这份状态还会串到别的 Area 上）。
 *
 * 现在**不持状态**：点一下就把锚点报上去，开还是关由宿主决定
 * （它才是菜单状态的唯一主人）。`aria-expanded` 也跟着去掉 ——
 * 拿不到真实状态时，写一个猜的布尔值就是撒谎；`aria-haspopup`
 * 描述的才是这个按钮**确实**有的语义。
 */


const editorLabel = computed(() => EDITOR_META[shownEditor.value].label)
</script>

<template>
  <!--
    ⚠️ `data-editor` 是**显示的那个**（现状），`data-editor-intent` 只在
    两者不同时出现 —— 也就是"这个窗口本来要显示 X，但那个包没装"。
    文档 / 测试 / 主题都按"显示的"来对，意图只用于解释与排查。
  -->
  <section
    ref="rootEl"
    class="np-area"
    :class="{ 'is-focused': focused }"
    :data-editor="shownEditor"
    :data-editor-intent="area.editor !== shownEditor ? area.editor : undefined"
    :data-area-id="area.id"
    :data-np-header-edge="regions.header.edge"
    @pointerdown.capture="emit('focus')"
  >
    <!--
      header Region。
      ⚠️ 它**不是**全局顶栏（那个在 PlaygroundHeader）。
      这是「这个 Area 自己的头」—— Blender 里每个 Area 都有一条。
    -->
    <!--
      ⚠️ 右键在 Area 头上要**拦住**：不 prevent 的话事件会冒到画布上，
      开出节点右键菜单 —— 那个菜单里的操作（对齐、加节点）在头上全是无意义的。
    -->
    <header
      ref="headerEl"
      v-if="regions.header.visible"
      class="np-area__header"
      @contextmenu.prevent="emit('areaMenu', $event)"
    >
      <!--
        ⚠️ 这一层是**横向可滚动**的（Blender 的 header 也是这样）。
        原来空间不够时会把右侧整组按钮收进一个「⋯」，等于**把功能藏起来**；
        滚动则是「都在，只是要划一下」—— 触摸端本来就会滑动，
        桌面端也有滚轮/触控板。所以这里用滚动代替收起。
      -->
      <div
        ref="headerScrollEl"
        class="np-area__header-scroll"
        @wheel="onHeaderWheel"
      >
      <!--
        编辑器类型：**图标 + 菜单**，不是标题文字。
        ⚠️ Blender 的做法。窗口一多，一排「节点图 / 节点图 / 输出」
        既占地方又难扫；图标能一眼分辨，文字留给 tooltip 和读屏
        （`aria-label` 仍然是完整名字，不是图标）。
      -->
      <!--
        ⚠️ **图标和标题是同一个按钮**（实测反馈：「窗口类型图标+标题应该
        支持一起点」）。上一版是图标按钮 + 旁边一个纯文本 span ——
        名字看起来像可以点，点下去却没反应。
      -->
      <button
        ref="editorBtnEl"
        class="np-area__editor-btn"
        type="button"
        :title="`窗口类型：${editorLabel}（点一下换）`"
        :aria-label="`窗口类型：${editorLabel}。点一下选择其他窗口类型`"
        aria-haspopup="menu"
        @click.stop="openEditorMenu"
      >
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" v-html="editorIcon" />
        <span class="np-area__editor-name">{{ editorLabel }}</span>
      </button>

      <span class="np-area__sep" />

      <!--
        编辑器专属的 header 内容（宿主通过 `#header-extra` 插槽给）。
        和「App 顶栏的菜单」不同：这些是**这个窗口自己的**开关 ——
        比如节点图的「适配缩放 / 手」，放在这里才不会和别的窗口混在一起。
      -->
      <slot name="headerExtra" :area="area" />

      <!--
        ⚠️ 容器是**真的 `<span>`**，不是裸 `<template>`。
        两个原因：
          · `<template>` 编译出来是 `<template>` 元素，内容进 document
            fragment、**不参与渲染**（分屏按钮曾经因此整个消失）；
          · 也没有布局盒子，`offsetWidth` 恒为 0 —— 而这里正要**量它的宽度**
            来判断放不放得下。

        收起来时整组 `v-if` 掉，只留右边那个「⋯」。
      -->
      <span ref="regionGroupEl" class="np-area__regions">
      <button
        v-for="region in headerRegionButtons"
        :key="region.key"
        class="np-area__icon"
        :class="{ 'is-on': area.regions[region.key] }"
        type="button"
        :title="`${region.label}（${area.regions[region.key] ? '已显示' : '已隐藏'}）`"
        :aria-pressed="area.regions[region.key]"
        @click="emit('toggleRegion', region.key)"
      >
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" v-html="region.icon" />
      </button>
      </span>

      <!--
        ⚠️ 侧栏开关**回到 header**（真机反馈："顶栏的菜单以及按钮能不能支持
        滚动？…这样就不用收到那 3 个点里面去了还不影响快捷使用"）。
        横滚条保证它们在任何宽度下都够得到，所以不需要再藏。

        ⚠️ 只在编辑器**声明了那一侧**时才出现（摆一个点了没反应的按钮更糟）；
        状态读写的是**布局**里的 `panels`（`AreaLeaf.panels`）——
        和右键菜单里那两项是同一份。
      -->
      <template v-if="panelSides.length">
        <span class="np-area__sep" />
        <button
          v-for="side in panelSides"
          :key="side"
          class="np-area__icon"
          :class="{ 'is-on': Boolean(activePanelOf(side)) }"
          type="button"
          :title="`${side === 'right' ? '右侧' : '左侧'}面板（${panelsOf[side]!.map((t) => t.label).join(' / ')}）`"
          :aria-pressed="Boolean(activePanelOf(side))"
          @click="togglePanelSide(side)"
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"
            :style="side === 'left' ? 'transform: scaleX(-1)' : undefined"
            v-html="'<rect x=\'3\' y=\'4\' width=\'18\' height=\'16\' rx=\'2\'/><path d=\'M15 4v16\'/>'" />
        </button>
      </template>

      <span ref="spacerEl" class="np-area__spacer" />

      <span ref="actionsEl" class="np-area__actions">
      </span>
      </div>

      <!--
        「拖出去分割」把手。
        ⚠️ 放在 header 里而不是 Area 的**角落**：四个角全都被占用了
        （右下是小地图、左下是工具栏、上面是 header 自身），
        放在角落里会和它们抢指针事件。放在这里零冲突，
        而且触摸端可以把命中区做得足够大。
        ⚠️ 位置在「关闭」**之前**：破坏性操作（关闭）留在最右端是通用约定，
        而且实测里测试是用 `:last-child` 找关闭按钮的 ——
        把手插到它后面会让那个选择器落空（已改成语义类名 `--close`，
        但顺序仍然按约定来）。

        拖到哪就分到哪（边界落在松手位置）—— 这是它相对
        「左右分屏 / 上下分屏」两个按钮唯一多出来的价值。
      -->
      <span
        class="np-area__split-grip"
        role="separator"
        :title="`点击 = 左右分屏；按住往左右 / 上下拖 = 按方向分屏（边界落在松手处）`"
        @pointerdown="onSplitGripDown"
      >
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true">
          <path d="M4 4v6M4 4h6M20 20v-6M20 20h-6" />
        </svg>
      </span>
      <!-- 同上：`js-area-close` 只是查询钩子，样式走 `.np-area__icon` -->
      <button
        class="np-area__icon js-area-close"
        type="button"
        :disabled="areaCount <= 1"
        :title="areaCount <= 1 ? '至少保留一个区域' : '关闭这个区域'"
        @click="emit('close')"
      >
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true">
          <path d="M6 6l12 12M18 6L6 18" />
        </svg>
      </button>

    </header>

    <!--
      ⚠️ `np-area__body` 上**没有**放置类，方向完全由根元素上的
      `data-np-toolbar-edge` 决定（见 `<style>` 里那一段）。
      这样做是为了让主题的一段任意 CSS 也能改它 —— 类名是固定的，
      属性值才是变量，选择器两边都写得出来。
    -->
    <div class="np-area__body">
      <!--
        左侧栏（Blender 的 T 面板）。
        ⚠️ 它和右侧栏都放在 `.np-area__body` 里、用 `order` 定位，
        **不用 `row-reverse`** —— 后者会让「左栏」在工具栏靠右时跑到右边去。
      -->
      <div v-if="activeLeft" class="np-area__panel np-area__panel--left">
        <div class="np-area__panel-tabs" role="tablist">
          <button
            v-for="t in panelsOf.left!"
            :key="t.id"
            class="np-area__panel-tab"
            :class="{ 'is-on': activeLeft.tab === t.id }"
            type="button"
            role="tab"
            :aria-selected="activeLeft.tab === t.id"
            :title="t.label"
            @click="selectTab('left', t.id)"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" v-html="t.icon" />
            <span>{{ t.label }}</span>
          </button>
        </div>
        <div class="np-area__panel-body">
          <slot name="panel" :area="area" side="left" :tab="activeLeft.tab" />
        </div>
      </div>

      <!-- 右侧栏（Blender 的 N 面板） -->
      <div v-if="activeRight" class="np-area__panel np-area__panel--right">
        <div class="np-area__panel-tabs" role="tablist">
          <button
            v-for="t in panelsOf.right!"
            :key="t.id"
            class="np-area__panel-tab"
            :class="{ 'is-on': activeRight.tab === t.id }"
            type="button"
            role="tab"
            :aria-selected="activeRight.tab === t.id"
            :title="t.label"
            @click="selectTab('right', t.id)"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" v-html="t.icon" />
            <span>{{ t.label }}</span>
          </button>
        </div>
        <div class="np-area__panel-body">
          <slot name="panel" :area="area" side="right" :tab="activeRight.tab" />
        </div>
      </div>

      <!-- main：编辑器内容 + 覆盖层（小地图 / 划痕） -->
      <div
        ref="bodyEl"
        class="np-area__main"
        @scroll.capture="onMainScroll"
      >
        <slot name="editor" :area="area" />
      </div>

    </div>

    <!--
      ⚠️ 这里原来有一个 **statusbar Region**（每个窗口自己的提示条）。
      它删掉了 —— 提示条现在是**全局唯一**的一条，在 `Playground.vue` 的
      `.pg__statusbar` 里（Blender 也是这样：提示条有一根，内容是
      「鼠标底下那个窗口」的）。每个 Area 一条会看到重复的提示，
      而且窗口越分越小、提示越铺越多。
    -->

    <!--
      分割预览线：跟着指针，告诉用户「松手之后边界会落在这里」。
      ⚠️ `position: fixed` + 屏幕坐标：拖拽期间它可能延伸到 Area 之外
      （用户拖到别的区域上去了），用 Area 内的相对定位会被裁掉。
    -->
    <span
      v-if="splitDrag"
      class="np-area__split-preview"
      :class="`np-area__split-preview--${splitDrag.dir}`"
      :style="
        splitDrag.dir === 'row'
          ? { left: `${splitDrag.x}px` }
          : { top: `${splitDrag.y}px` }
      "
    />
</section>
</template>

<style scoped>
.np-area {
  position: relative;
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
  /*
   * 底色走 `--np-shell-bg`（外壳配方设的），不是直接读 `--np-surface-0`。
   * 这样主题能通过「外壳」配方一次改掉所有 Area 的底色，
   * 而不用去改调色令牌本身（那会连带改掉别的用到 surface-0 的地方）。
   */
  background-color: var(--np-shell-bg, var(--np-surface-0, #1b1b1b));
  background-image: var(--np-shell-veil, none);
}

/*
 * 焦点环。
 * ⚠️ 用 `inset box-shadow` 而不是 `border` —— 加边框会改变盒子尺寸，
 * 拖动分隔条时两侧宽度会跳。
 */
.np-area.is-focused::after {
  content: '';
  position: absolute;
  inset: 0;
  z-index: var(--np-z-chrome, 30);
  pointer-events: none;
  box-shadow: inset 0 0 0 1px var(--np-accent-soft, rgba(240, 160, 75, 0.16));
}

/* ── header Region ─────────────────────────────────────────────── */

.np-area__header {
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  /* ⚠️ 裁掉溢出，不然会顶到隔壁 Area 上。滚动交给内层。 */
  overflow: hidden;
  flex: 0 0 auto;
  /*
   * ⚠️ **高度定死**（`height`，不是 `min-height`）：包通过 `header` 贡献控件
   * （打印包的编辑 / 适应 / 1:1），它们**不许**把窗口头撑高 ——
   * 真机反馈："节点图的倒是正常，页面编辑器为什么会抬高顶栏？这个功能应该是
   * 窗口的基础功能，不是某个窗口的功能"。
   *
   * 撑高的后果不只是难看：一排窗口的头部高度不一致，正文区的起点就会错位，
   * 而且顶栏一变高，画布的可视高度也跟着变（用户改一个包就动了内核的排版）。
   */
  height: var(--np-ctl-h-md, 26px);
  min-height: 0;
  border-bottom: 1px solid var(--np-border-bar, rgba(0, 0, 0, 0.55));
  background: var(--np-surface-bar-soft, #212121);
  color: var(--np-panel-dim, #9a9a9a);
  font-size: 10.5px;
}

/*
 * ⚠️ **横向滚动的那一层**（Blender 的 header 也是这样）。
 *
 * 它取代了原来「放不下就把整组按钮收进 ⋯」的做法。差别不是美观：
 * 收起之后一个功能在窄窗口里**根本不存在**，而滚动是"都在，只是要划一下"。
 *
 * ⚠️ 滚动条**藏起来**（`scrollbar-width: none` + `::-webkit-scrollbar`）：
 * 一条 6px 的横向滚动条会把 header 撑高，而 header 的高度是
 * `--np-ctl-h-md` 定死的 —— 撑高会让所有窗口的头部高度不一致。
 */
.np-area__header-scroll {
  display: flex;
  align-items: center;
  /*
   * ⚠️ **不许换行**：装不下要**横向溢出**（走滚动），不是折成两行 ——
   * 折行会让头部变高（高度是定死的，所以只会把内容压扁/叠起来）。
   */
  flex-wrap: nowrap;
  gap: 3px;
  flex: 1 1 auto;
  min-width: 0;
  align-self: stretch;
  padding: 0 6px;
  overflow-x: auto;
  overflow-y: hidden;
  overscroll-behavior-x: contain;
  scrollbar-width: none;
  /*
   * ⚠️ `touch-action: pan-x` 是**触摸横滑**的关键：不给它的话，
   * 手指在 header 上横划会被当成 pointerdown 落在按钮上（按钮"粘手"）。
   * 纵向留给页面（`pan-x` 只接管横向）。
   */
  touch-action: pan-x;
}

.np-area__header-scroll::-webkit-scrollbar {
  display: none;
}

/*
 * 窗口类型按钮：**图标**，不是标题文字。
 * ⚠️ 名字仍然在（`aria-label` + `title`），只是不占 header 的横向空间。
 */
.np-area__editor-btn {
  display: flex;
  align-items: center;
  /* ⚠️ 图标 + 标题在同一个按钮里，所以是横排、有间距，不再是正方形 */
  gap: 5px;
  flex: 0 0 auto;
  height: calc(var(--np-ctl-h-md, 26px) - 6px);
  padding: 0 6px 0 4px;
  border: 0;
  border-radius: var(--np-radius-s, 4px);
  background: transparent;
  color: var(--np-panel-text, #e2e2e2);
  cursor: pointer;
}

.np-area__editor-btn:hover,
.np-area__editor-btn[aria-expanded='true'] {
  background: var(--np-surface-3);
}

/*
 * 窗口类型的**文字**只在大屏出现 —— 它就是被图标取代的那个标题。
 * 小屏上连它一起收掉：一排窗口挤在手机上时，每个 header 都顶着一个
 * 词只会让可滚动的区域更短。
 */
.np-area__editor-name {
  color: var(--np-panel-text, #e2e2e2);
  white-space: nowrap;
}

@media (max-width: 720px) {
  .np-area__editor-name {
    display: none;
  }
}

/* ══════════════════════════════════════════════════════════════════
   侧栏 tab（Blender 的 N / T 面板）

   ⚠️ 位置用 `order`，**不用 `flex-direction: row-reverse`**。
   后者是为了「工具栏靠右」写的，它会把整个 body 反向 ——
   于是「左栏」在工具栏靠右时跑到右边去。`order` 每个子项各自说话，
   不会互相牵制。
   ══════════════════════════════════════════════════════════════════ */

.np-area__panel {
  display: flex;
  flex: 0 0 auto;
  min-height: 0;
  background: var(--np-surface-bar-soft, #212121);
}

.np-area__panel--left {
  order: -1;
  flex-direction: row-reverse;
  border-right: 1px solid var(--np-border-bar, rgba(0, 0, 0, 0.55));
}

.np-area__panel--right {
  order: 1;
  flex-direction: row;
  border-left: 1px solid var(--np-border-bar, rgba(0, 0, 0, 0.55));
}

/* 竖排 tab 条 —— Blender 的侧栏就是这一条 */
.np-area__panel-tabs {
  display: flex;
  flex-direction: column;
  flex: 0 0 auto;
  gap: 2px;
  padding: 4px 3px;
  overflow: hidden;
}

.np-area__panel-tab {
  display: flex;
  align-items: center;
  gap: 4px;
  flex: 0 0 auto;
  padding: 6px 3px;
  border: 0;
  border-radius: var(--np-radius-s, 4px);
  background: transparent;
  color: var(--np-panel-dim, #9a9a9a);
  font: inherit;
  font-size: 10.5px;
  /* 文字竖排：Blender 的 tab 标签是竖着的，省横向空间 */
  writing-mode: vertical-rl;
  cursor: pointer;
}

.np-area__panel-tab svg {
  /* 图标不跟着转 */
  writing-mode: horizontal-tb;
}

.np-area__panel-tab:hover {
  background: var(--np-surface-3);
  color: var(--np-panel-text, #e2e2e2);
}

.np-area__panel-tab.is-on {
  background: var(--np-accent-soft);
  color: var(--np-accent);
}

.np-area__panel-body {
  /*
   * ⚠️ 面板宽度**固定**，不吃 flex 剩余空间 —— 侧栏是"附加信息"，
   * 让它和画布抢宽度会让画布在开关侧栏时明显跳一下。
   */
  width: 232px;
  min-width: 0;
  overflow: hidden auto;
  overscroll-behavior: contain;
  color: var(--np-panel-text, #e2e2e2);
}

/* 窄窗口里侧栏占满宽会更实用（没有"旁边"可言） */
@media (max-width: 720px) {
  .np-area__panel-body {
    width: min(232px, 70vw);
  }
}

/*
 * ⚠️ 原生 select 的下拉项是**系统渲染**的，`var()` 在部分浏览器上取不到 ——
 * 显式给 option 一个不透明的底色/字色，否则浅色主题下会变成白底白字。
 */
.np-area__editor option {
  background: var(--np-surface-popup, #202020);
  color: var(--np-panel-text, #e2e2e2);
}



/*
 * 头部按钮组。
 *
 * ⚠️ `flex: 0 0 auto` 是**溢出检测的前提**：它不参与收缩，
 * 所以空间不够时 header 会真的溢出（`scrollWidth > clientWidth`），
 * 我们据此换成「⋯」。允许它收缩的话按钮会被压扁而不是溢出，
 * 检测永远不触发。
 *
 * ⚠️ `overflow: hidden` 让溢出**被裁掉**而不是顶出容器 ——
 * 否则按钮会画到 Area 外面去（压在旁边那个 Area 上）。
 */
.np-area__actions,
.np-area__regions {
  display: flex;
  align-items: center;
  gap: 3px;
  flex: 0 0 auto;
  overflow: hidden;
}

.np-area__spacer {
  flex: 1 1 auto;
}

/*
 * ⚠️ `.np-area__icon` / `.np-area__sep` 的样式**已搬到 `theme/np-area.css`**
 * （全局）：包通过插槽放进去的按钮拿不到这个 scoped 块的 `data-v-*`，
 * 留在这里的话包那边就掉回浏览器默认按钮（灰底、更宽）。
 * 一句话：**包会用到的类，样式必须在全局那份里。**
 */

/* ── body：toolbar | main | sidebar ───────────────────────────── */

.np-area__body {
  display: flex;
  flex: 1 1 auto;
  min-width: 0;
  min-height: 0;
}

/* ══════════════════════════════════════════════════════════════════
   区域放置（由主题决定，见 `layout/regionPlacement.ts`）

   ⚠️ 方向全部由**根元素上的属性**驱动，不写死 flex 顺序。

   上一版这里只有一句 `flex-direction: column`（工具栏永远在左边）——
   那个写法把「工具栏在哪」变成了**模板结构**，于是主题就算知道
   用户想要它在右边，也无处表达。改成属性驱动之后：

     · 摆位是数据（`data-np-toolbar-edge`），主题能改；
     · 主题写一段任意 CSS 也能改（属性选择器是稳定的）；
     · 模板结构不变，所以换主题**不会重建 Area**
       （重建会让 VueFlow 的视口和选中全丢 —— 见 `presets.ts` 的注释）。
   ══════════════════════════════════════════════════════════════════ */




/* 工具栏自身的排布方向跟着它靠哪条边走：贴左右时竖排，贴上下时横排 */

/* ⚠️ 显式排布方向 —— 主题可以覆盖「跟着边走」的默认 */


/* header 换边：只调 flex 顺序，不动 DOM */
.np-area[data-np-header-edge='bottom'] .np-area__header {
  order: 3;
}

/*
 * ── 浮动（`mode: 'float'`）──────────────────────────────────────
 *
 * ⚠️ 两条路，差别只在 `reserveSpace`：
 *
 *   reserveSpace = 1（默认）视觉浮起 —— 留白 / 圆角 / 阴影，
 *                          但**仍然占位**。画布拿到的还是真实剩余空间，
 *                          `fitView()` 不需要知道任何新东西。零风险。
 *
 *   reserveSpace = 0        真覆盖画布。这时 `main` 靠
 *                          `--np-region-inset-*` 让出内边距 ——
 *                          不让就是重演「节点被塞进面板底下」那个事故。
 */



/* 真覆盖时画布让出厚度 —— 否则 `fitView()` 会把节点送到工具栏底下 */




/*
 * ⚠️ toolbar 是 main 的**兄弟**，不浮在它上面。
 * 画布（main）拿到的是真实的剩余空间 —— 这就是 `fitView()` 从此
 * 不会把节点塞进面板底下的原因。
 *
 * ⚠️ **触摸端不再把它变成抽屉**（那一版做过，已删除）。
 * 用户实测后的要求是「显示的时候就是固定的」：
 *   · 抽屉要理解「点暗处关闭」，而暗区看起来只是变暗的画布
 *     （实测反馈：「遮罩没看懂是显示什么」）；
 *   · 抽屉一开一关，画布宽度在「整宽 / 少 144px」之间跳，`fitView` 的
 *     结果跟着跳，很难受。
 * 固定占位只有一个缺点（少 144px 宽），但它是**可预期**的。
 */

.np-area__main {
  position: relative;
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-width: 0;
  min-height: 0;
}

/* ── statusbar Region ─────────────────────────────────────────── */

/* ── 拖出去分割的把手 + 预览线 ─────────────────────────────── */

/*
 * 把手：header 最右端的一个小方块。
 *
 * ⚠️ 视觉上只有 18px，但**命中区**要撑大 —— 它紧挨着「关闭」按钮，
 * 点错就关掉整个区域。用 `::after` 往外撑 4px 而不是把可见尺寸做大，
 * 免得 header 上一排按钮宽窄不一。
 */
.np-area__split-grip {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 18px;
  height: 18px;
  border-radius: var(--np-radius-s, 3px);
  color: var(--np-panel-dim);
  cursor: grab;
  touch-action: none;
}

.np-area__split-grip::after {
  content: '';
  position: absolute;
  inset: -3px;
}

.np-area__split-grip:hover {
  background: var(--np-surface-3);
  color: var(--np-panel-text);
}

.np-area__split-grip:active {
  cursor: grabbing;
}

/*
 * 预览线：跟着指针的一条强调色细线。
 * `pointer-events: none` 是必须的 —— 它盖在画布上，
 * 吃掉事件的话拖拽中途就会被它截断。
 */
.np-area__split-preview {
  position: fixed;
  z-index: var(--np-z-overlay, 20);
  background: var(--np-accent, #f0a04b);
  pointer-events: none;
  opacity: var(--np-opacity-strong);
}

.np-area__split-preview--row {
  top: 0;
  bottom: 0;
  width: 2px;
  margin-left: -1px;
}

.np-area__split-preview--col {
  left: 0;
  right: 0;
  height: 2px;
  margin-top: -1px;
}

/* 触摸端：把手要够大，而且手指按住时不该触发系统手势 */
@media (pointer: coarse) {
  .np-area__split-grip {
    width: 30px;
    height: 26px;
  }
}

/*
 * ⚠️ `.np-area__statusbar` 删掉了 —— 提示条现在是**全局唯一**的一条
 * （`Playground.vue` 的 `.pg__statusbar`），不再是每个窗口的 Region。
 */
</style>
